import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle } from 'lucide-react';
import { usePracticeReviewFunnel } from '../../hooks';
import type {
  FunnelRange,
  FunnelStepKey,
  FunnelStepRow,
  GeneratedDraft,
  ReviewFunnel,
} from '../../api/commandCenterApi';
import styles from './ReviewFunnelTab.module.css';
import { TextFunnelPanel } from './TextFunnelPanel';
import { CompareMethodsPanel } from './CompareMethodsPanel';

const RANGES: FunnelRange[] = ['7d', '30d', '90d'];

const STEP_HINTS: Record<FunnelStepKey, string> = {
  scan: 'Short link minted',
  open: 'Capture page rendered',
  rating: 'Branch point',
  people: 'Staff picked → Next',
  reason: 'Visit reason → Next',
  standout: 'Theme picked → Next',
  detail: 'Supporting details → Next',
  own_words: 'Own words → Continue/Skip',
  expectation: 'Posting steps shown → I understand',
  draft: 'Assembled review rendered',
  posted: 'Tapped Open Google',
};

const ALL_LOCATIONS = '';

function fmtPct(v: number): string {
  return (v >= 10 ? Math.round(v) : Math.round(v * 10) / 10) + '%';
}

function fmtSecs(s: number): string {
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
}

/** Percentage change vs the previous window; null when there's no baseline. */
function delta(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

function DeltaLabel({ value }: { value: number | null }) {
  if (value === null) return <span className={clsx(styles.delta, styles.deltaFlat)}>—</span>;
  const dir = value > 1 ? 'up' : value < -1 ? 'down' : 'flat';
  const cls = dir === 'up' ? styles.deltaUp : dir === 'down' ? styles.deltaDown : styles.deltaFlat;
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '–';
  return (
    <span className={clsx(styles.delta, cls)}>
      {arrow} {Math.abs(value)}%
    </span>
  );
}

// ─── Step rows ────────────────────────────────────────────────────────────────

function StepRow({
  row,
  index,
  scans,
  isWorst,
  isSelected,
  onSelect,
}: {
  row: FunnelStepRow;
  index: number;
  scans: number;
  isWorst: boolean;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const trackWidth = scans > 0 ? (row.eligible / scans) * 100 : 0;
  const isFinal = row.step === 'posted';

  return (
    <button
      type="button"
      className={clsx(styles.step, isSelected && styles.stepActive)}
      aria-pressed={isSelected}
      onClick={onSelect}
      title={`${row.label}: ${row.count} of ${row.eligible} eligible (${fmtPct(row.pctOfEligible)})`}
    >
      <span className={styles.stepIdx}>{index + 1}</span>
      <span className={styles.stepName}>
        {row.label}
        <em className={styles.stepHint}>{STEP_HINTS[row.step]}</em>
      </span>
      <span className={styles.bar}>
        <span className={styles.barTrack} style={{ width: `${trackWidth}%` }} />
        <span
          className={isFinal ? styles.barFillFinal : styles.barFill}
          style={{ width: `${row.pctOfScans}%` }}
        />
      </span>
      <span className={styles.stepValue}>{row.count}</span>
      <span className={styles.stepPct}>{fmtPct(row.pctOfScans)}</span>
      <span className={isWorst ? styles.stepDropWorst : styles.stepDrop}>
        {index === 0 ? '—' : `−${row.lost}`}
      </span>
    </button>
  );
}

function StepTable({ steps }: { steps: FunnelStepRow[] }) {
  return (
    <table className={styles.dataTable}>
      <thead>
        <tr>
          <th>Step</th>
          <th>Count</th>
          <th>Of scans</th>
          <th>Step conv.</th>
          <th>Lost</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((row, i) => (
          <tr key={row.step}>
            <td>
              {i + 1}. {row.label}
            </td>
            <td>{row.count}</td>
            <td>{fmtPct(row.pctOfScans)}</td>
            <td>{i === 0 ? '—' : fmtPct(row.pctOfEligible)}</td>
            <td>{i === 0 ? '—' : `−${row.lost}`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Side rail ────────────────────────────────────────────────────────────────

function StepDetailCard({ row, isWorst }: { row: FunnelStepRow; isWorst: boolean }) {
  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>Step detail</div>
      <div className={styles.cardTitle}>{row.label}</div>
      <div className={styles.cardSub}>{STEP_HINTS[row.step]}</div>
      <dl className={styles.defList}>
        <div className={styles.defRow}>
          <dt>Reached</dt>
          <dd>{row.count}</dd>
        </div>
        <div className={styles.defRow}>
          <dt>Eligible to reach</dt>
          <dd>{row.eligible}</dd>
        </div>
        <div className={styles.defRow}>
          <dt>Of those eligible</dt>
          <dd>{fmtPct(row.pctOfEligible)}</dd>
        </div>
        <div className={styles.defRow}>
          <dt>Lost here</dt>
          <dd className={isWorst ? styles.defValueWorst : undefined}>{row.lost}</dd>
        </div>
        <div className={styles.defRow}>
          <dt>Median time from previous</dt>
          <dd>{row.medianSecondsFromPrev === null ? '—' : fmtSecs(row.medianSecondsFromPrev)}</dd>
        </div>
        {/* Render-view instrumentation: "saw the screen" vs "cleared it"
            separates a patient who never got here from one who bailed here.
            Null on non-wizard steps and on data recorded before view events. */}
        {row.viewed !== null && (
          <>
            <div className={styles.defRow}>
              <dt>Saw this screen</dt>
              <dd>{row.viewed}</dd>
            </div>
            <div className={styles.defRow}>
              <dt>Saw it, didn&apos;t clear it</dt>
              <dd className={row.viewed - row.count > 0 ? styles.defValueWorst : undefined}>
                {Math.max(row.viewed - row.count, 0)}
              </dd>
            </div>
            <div className={styles.defRow}>
              <dt>Median time on screen</dt>
              <dd>{row.medianDwellSeconds === null ? '—' : fmtSecs(row.medianDwellSeconds)}</dd>
            </div>
          </>
        )}
      </dl>
    </div>
  );
}

function WeeklyTrendCard({ weekly }: { weekly: ReviewFunnel['weekly'] }) {
  const max = Math.max(1, ...weekly.map((w) => w.scans));

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>Weekly scans &amp; posts</div>
      {weekly.length === 0 ? (
        <div className={styles.cardSub}>No activity in this window yet.</div>
      ) : (
        <>
          <div className={styles.spark}>
            {weekly.map((w, i) => (
              <div
                key={w.weekStart}
                className={styles.sparkBar}
                title={`Week of ${w.weekStart} — ${w.scans} scans, ${w.posted} posted`}
              >
                <i
                  className={i === weekly.length - 1 ? styles.sparkFillLast : styles.sparkFill}
                  style={{ height: `${Math.max(6, (w.scans / max) * 100)}%` }}
                />
              </div>
            ))}
          </div>
          <div className={styles.sparkAxis}>
            <span>{weekly[0].weekStart}</span>
            <span>this week</span>
          </div>
        </>
      )}
    </div>
  );
}

function RatingMixCard({ mix }: { mix: Record<string, number> }) {
  const total = Object.values(mix).reduce((a, b) => a + b, 0);

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>Star ratings given</div>
      <div className={styles.dist}>
        {[5, 4, 3, 2, 1].map((star) => {
          const n = mix[String(star)] ?? 0;
          const fill =
            star === 5 ? styles.meterFillGood : star === 4 ? styles.meterFill : styles.meterFillLow;
          return (
            <div key={star} className={styles.distRow}>
              <span className={styles.distLabel}>{star}★</span>
              <span className={styles.meter}>
                <i className={fill} style={{ width: total > 0 ? `${(n / total) * 100}%` : '0%' }} />
              </span>
              <span className={styles.distCount}>{n}</span>
            </div>
          );
        })}
      </div>
      <div className={styles.cardSub}>
        {total === 0
          ? 'No ratings in this window.'
          : `${fmtPct(((mix['5'] ?? 0) / total) * 100)} of patients who rated gave 5★ and entered the wizard.`}
      </div>
    </div>
  );
}

function OutcomesCard({ funnel }: { funnel: ReviewFunnel }) {
  const opened = funnel.steps.find((s) => s.step === 'open')?.count ?? 0;
  const rows = [
    // Tapping through opens Google in another tab, which we can't observe — so
    // the tap and the patient's own "yes, I posted it" are reported separately.
    { label: 'Tapped Open Google', n: funnel.outcomes.posted, fill: styles.meterFillInfo },
    { label: 'Confirmed posted', n: funnel.outcomes.postConfirmed, fill: styles.meterFillGood },
    { label: 'Private feedback', n: funnel.outcomes.privateFeedback, fill: styles.meterFillInfo },
    { label: 'Left without finishing', n: funnel.outcomes.abandoned, fill: styles.meterFillLow },
  ];

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>Where the {opened} opened sessions ended</div>
      <div className={styles.dist}>
        {rows.map((r) => (
          <div key={r.label} className={styles.distRowWide}>
            <span className={styles.distLabel}>{r.label}</span>
            <span className={styles.meter}>
              <i
                className={r.fill}
                style={{ width: opened > 0 ? `${(r.n / opened) * 100}%` : '0%' }}
              />
            </span>
            <span className={styles.distCount}>{r.n}</span>
          </div>
        ))}
      </div>
      <div className={styles.cardSub}>
        Each session counts once — a patient who posts to Google and then also writes privately
        counts as posted. &ldquo;Confirmed posted&rdquo; is a subset of the taps: only those
        patients came back and told us the review went live.
      </div>
    </div>
  );
}

const DRAFT_OUTCOMES: Record<
  GeneratedDraft['outcome'],
  { label: string; className: keyof typeof styles }
> = {
  confirmed: { label: 'Confirmed on Google', className: 'draftOutcomeConfirmed' },
  handed_off: { label: 'Tapped Open Google', className: 'draftOutcomeHandedOff' },
  private: { label: 'Went private instead', className: 'draftOutcomePrivate' },
  not_posted: { label: 'Not posted', className: 'draftOutcomeNotPosted' },
};

/**
 * The actual review text the tool generated per session, from the invite audit
 * log — including drafts that never made it to Google, which are otherwise
 * invisible (the only other copy is on the patient's phone).
 */
function GeneratedDraftsCard({ drafts }: { drafts: GeneratedDraft[] }) {
  return (
    <div className={styles.draftsCard}>
      <div className={styles.cardHead}>Generated drafts</div>
      <div className={styles.cardSub}>
        What the tool actually wrote for each session in this window, newest first. The patient may
        have edited before posting.
      </div>
      {drafts.length === 0 ? (
        <div className={styles.cardSub}>No drafts were generated in this window.</div>
      ) : (
        <div className={styles.draftList}>
          {drafts.map((d) => {
            const outcome = DRAFT_OUTCOMES[d.outcome];
            return (
              <div key={d.shortCode} className={styles.draftItem}>
                <div className={styles.draftMeta}>
                  <span className={styles[outcome.className]}>{outcome.label}</span>
                  {d.isTest && <span className={styles.draftTestPill}>Staff test</span>}
                  {d.patientName ? (
                    <span className={styles.draftPatientName}>{d.patientName}</span>
                  ) : (
                    <span
                      className={styles.draftPatientUnknown}
                      title="This patient was never asked for a name — 5★ sessions go straight to Google."
                    >
                      Name not given
                    </span>
                  )}
                  {d.rating !== null && <span>{d.rating}★</span>}
                  <span>{new Date(d.createdAt).toLocaleDateString()}</span>
                  {d.archetype && <span>{d.archetype.replace(/_/g, ' ')}</span>}
                </div>
                <div className={styles.draftText}>{d.draftText}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

/**
 * `locationId` pins the funnel to one location and hides the location picker —
 * the Modules tab mounts it that way. Without it the funnel covers the whole
 * practice and the operator picks a location from the toolbar.
 */
function QrFunnelPanel({
  practiceId,
  locationId: fixedLocationId,
}: {
  practiceId: string;
  locationId?: string;
}) {
  const [range, setRange] = useState<FunnelRange>('30d');
  const [pickedLocationId, setLocationId] = useState<string>(ALL_LOCATIONS);
  const locationId = fixedLocationId ?? pickedLocationId;
  const [selectedStep, setSelectedStep] = useState<FunnelStepKey | null>(null);
  const [tableView, setTableView] = useState(false);

  const params = useMemo(
    // Link & QR only: text sessions have no scan and their own step order (see TextFunnelPanel).
    () => ({ range, channel: 'manual_qr' as const, ...(locationId ? { locationId } : {}) }),
    [range, locationId],
  );
  const { data: funnel, isLoading, isError } = usePracticeReviewFunnel(practiceId, params);

  const locations = funnel?.locations ?? [];
  const steps = funnel?.steps ?? [];
  const scans = steps[0]?.count ?? 0;

  const activeStepKey = selectedStep ?? funnel?.biggestDropStep ?? steps[0]?.step ?? null;
  const activeStep = steps.find((s) => s.step === activeStepKey) ?? steps[0];

  const controls = (
    <div className={styles.toolbar}>
      <div>
        <div className={styles.toolbarTitle}>Review Accelerator funnel (link &amp; QR)</div>
        <div className={styles.toolbarSubtitle}>
          Every patient who scanned the QR code or opened the link, step by step
        </div>
      </div>
      <div className={styles.grow} />
      {fixedLocationId === undefined && locations.length > 1 && (
        <select
          className={styles.select}
          aria-label="Location"
          value={locationId}
          onChange={(e) => {
            setLocationId(e.target.value);
            setSelectedStep(null);
          }}
        >
          <option value={ALL_LOCATIONS}>All locations ({locations.length})</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.city ? `${l.name} — ${l.city}` : l.name}
            </option>
          ))}
        </select>
      )}
      <div className={styles.seg} role="group" aria-label="Date range">
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            className={clsx(styles.segBtn, range === r && styles.segBtnActive)}
            aria-pressed={range === r}
            onClick={() => {
              setRange(r);
              setSelectedStep(null);
            }}
          >
            {r}
          </button>
        ))}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className={styles.panel}>
        {controls}
        <div className={styles.empty}>Loading funnel…</div>
      </div>
    );
  }

  if (isError || !funnel || !activeStep) {
    return (
      <div className={styles.panel}>
        {controls}
        <div className={styles.empty}>
          {isError ? 'Could not load the review funnel.' : 'No funnel data yet.'}
        </div>
      </div>
    );
  }

  // Funnel tracking started at launch — there is no history before the first
  // recorded event, so say that rather than showing a wall of zeros.
  if (funnel.firstEventAt === null) {
    return (
      <div className={styles.panel}>
        {controls}
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>No funnel data yet</div>
          Step tracking starts the first time a patient scans this practice&apos;s review QR code.
        </div>
      </div>
    );
  }

  const worstStep = funnel.biggestDropStep;
  const worstRow = steps.find((s) => s.step === worstStep);
  const prevOfWorst = worstRow
    ? steps[steps.findIndex((s) => s.step === worstStep) - 1]
    : undefined;
  const privateTotal = funnel.outcomes.privateFeedback;
  const rated = steps.find((s) => s.step === 'rating')?.count ?? 0;
  // Headline numbers use the confirmed count: a tap only proves we opened
  // Google in another tab, and we never see what happened there.
  const tapped = funnel.outcomes.posted;
  const posted = funnel.outcomes.postConfirmed;

  return (
    <div className={styles.panel}>
      {controls}

      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Scans</div>
          <div className={styles.kpiValue}>{scans}</div>
          <div className={styles.kpiMeta}>
            <DeltaLabel value={delta(scans, funnel.previous.scans)} /> links opened from QR
          </div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Reached rating</div>
          <div className={styles.kpiValue}>{rated}</div>
          <div className={styles.kpiMeta}>
            <DeltaLabel value={delta(rated, funnel.previous.rated)} />{' '}
            {scans > 0 ? `${fmtPct((rated / scans) * 100)} of scans` : 'of scans'}
          </div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Confirmed posted</div>
          <div className={styles.kpiValue}>{posted}</div>
          <div className={styles.kpiMeta}>
            <DeltaLabel value={delta(posted, funnel.previous.postConfirmed)} /> of {tapped} tapped
            through
          </div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Scan → Google</div>
          <div className={styles.kpiValue}>{scans > 0 ? fmtPct((posted / scans) * 100) : '—'}</div>
          <div className={styles.kpiMeta}>end-to-end conversion</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Private feedback</div>
          <div className={styles.kpiValue}>{privateTotal}</div>
          <div className={styles.kpiMeta}>
            <DeltaLabel value={delta(privateTotal, funnel.previous.privateFeedback)} /> kept off
            Google
          </div>
        </div>
      </div>

      {worstRow && prevOfWorst && worstRow.lost > 0 && (
        <div className={styles.callout}>
          <AlertTriangle size={15} aria-hidden />
          <span>
            Biggest drop-off is{' '}
            <strong>
              {/* `people` follows the ≤4★ branch, so its pool is 5★ starts —
                  naming the previous row there would misstate the comparison. */}
              {worstRow.step === 'people' ? '5★ starts' : prevOfWorst.label} → {worstRow.label}
            </strong>
            : <strong>{worstRow.lost} patients</strong> ({fmtPct(100 - worstRow.pctOfEligible)} of
            those who got there) stopped here.
          </span>
        </div>
      )}

      <div className={styles.body}>
        <div>
          {tableView ? (
            <StepTable steps={steps} />
          ) : (
            <>
              <div className={styles.colHead}>
                <span className={styles.colHeadCell} />
                <span className={styles.colHeadCell}>Step</span>
                <span className={styles.colHeadCell} />
                <span className={styles.colHeadCellRight}>Count</span>
                <span className={styles.colHeadCellRight}>Of scans</span>
                <span className={styles.colHeadCellRight}>Drop-off</span>
              </div>
              <div className={styles.steps}>
                {steps.map((row, i) => (
                  <div key={row.step}>
                    <StepRow
                      row={row}
                      index={i}
                      scans={scans}
                      isWorst={row.step === worstStep}
                      isSelected={row.step === activeStepKey}
                      onSelect={() => setSelectedStep(row.step)}
                    />
                    {row.step === 'rating' && (
                      <div className={styles.branch}>
                        <span className={styles.branchTag}>Branch</span>
                        <span>
                          <strong>{funnel.branch.fiveStar}</strong> gave 5★ → guided wizard
                        </span>
                        <span>·</span>
                        <span>
                          <strong>{funnel.branch.lowStar}</strong> gave ≤4★ → straight to private
                          feedback (<strong>{funnel.branch.privateFromLowStar}</strong> submitted)
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
          <button type="button" className={styles.linkBtn} onClick={() => setTableView((v) => !v)}>
            {tableView ? 'Show as chart' : 'Show as table'}
          </button>
        </div>

        <div className={styles.rail}>
          <StepDetailCard row={activeStep} isWorst={activeStep.step === worstStep} />
          <WeeklyTrendCard weekly={funnel.weekly} />
        </div>
      </div>

      <div className={styles.bottomGrid}>
        <RatingMixCard mix={funnel.ratingMix} />
        <OutcomesCard funnel={funnel} />
        <GeneratedDraftsCard drafts={funnel.drafts ?? []} />
      </div>
    </div>
  );
}

// ─── Channel switch ───────────────────────────────────────────────────────────

type Channel = 'text' | 'qr' | 'compare';
const CHANNELS: Array<{ value: Channel; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'qr', label: 'Link & QR' },
  { value: 'compare', label: 'Compare' },
];

/**
 * The Reviews module's analytics: the text funnel, the link & QR funnel, or both side by side.
 * Without text review requests (the reviewsSms flag) it is the link & QR funnel alone.
 */
export function ReviewFunnelTab({
  practiceId,
  locationId,
  textAvailable = false,
}: {
  practiceId: string;
  locationId?: string;
  textAvailable?: boolean;
}) {
  // Null until the operator picks: the default follows the flag, which may load after first render.
  const [picked, setChannel] = useState<Channel | null>(null);
  const channel: Channel = picked ?? (textAvailable ? 'text' : 'qr');
  const [range, setRange] = useState<FunnelRange>('30d');
  if (!textAvailable) return <QrFunnelPanel practiceId={practiceId} locationId={locationId} />;
  return (
    <div className={styles.channelWrap}>
      <div className={styles.seg} role="group" aria-label="How patients were asked">
        {CHANNELS.map((c) => (
          <button
            key={c.value}
            type="button"
            className={clsx(styles.segBtn, channel === c.value && styles.segBtnActive)}
            aria-pressed={channel === c.value}
            onClick={() => setChannel(c.value)}
          >
            {c.label}
          </button>
        ))}
      </div>
      {channel === 'qr' && <QrFunnelPanel practiceId={practiceId} locationId={locationId} />}
      {channel === 'text' && (
        <TextFunnelPanel
          practiceId={practiceId}
          locationId={locationId}
          range={range}
          onRangeChange={setRange}
        />
      )}
      {channel === 'compare' && (
        <CompareMethodsPanel
          practiceId={practiceId}
          locationId={locationId}
          range={range}
          onRangeChange={setRange}
        />
      )}
    </div>
  );
}
